import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Button } from './Button';
import { COLORS } from '../config/Colors';

export class AutoSpinPopup extends Container {
  private background: Graphics;
  private contentContainer: Container;
  private closeButton!: Button;
  private titleText!: Text;
  private roundsText!: Text;
  private rounds: number = 10;
  private onConfirmCallback?: (rounds: number) => void;
  
  constructor() {
    super();
    
    this.background = new Graphics();
    this.contentContainer = new Container();
    
    this.drawBackground();
    this.createTitle();
    this.createRoundsDisplay();
    this.createButtons();
    this.createCloseButton();
    
    this.visible = false;
    this.zIndex = 250;
  }
  
  private drawBackground(): void {
    const width = 500;
    const height = 400;
    
    // Semi-transparent overlay
    this.background.rect(-384, -512, 768, 1024);
    this.background.fill({ color: 0x000000, alpha: 0.7 });
    
    // Main popup background
    this.background.roundRect(-width/2, -height/2, width, height, 20);
    this.background.fill({ color: 0xFFF8DC }); // Cornsilk (light parchment)
    this.background.stroke({ color: 0xDAA520, width: 4 }); // Goldenrod border
    
    this.addChild(this.background);
    this.addChild(this.contentContainer);
  }
  
  private createTitle(): void {
    const titleStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 28,
      fontWeight: 'bold',
      fill: COLORS.PRIMARY_DARK,
      stroke: { color: 0xFFFFFF, width: 2 }
    });
    
    this.titleText = new Text({ text: '🔄 Auto Spin', style: titleStyle });
    this.titleText.anchor.set(0.5);
    this.titleText.x = 0;
    this.titleText.y = -160;
    this.contentContainer.addChild(this.titleText);
  }
  
  private createRoundsDisplay(): void {
    const labelStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 18,
      fill: COLORS.TEXT_PRIMARY
    });
    
    const label = new Text({ text: 'Number of rounds:', style: labelStyle });
    label.anchor.set(0.5);
    label.x = 0;
    label.y = -80;
    this.contentContainer.addChild(label);
    
    // Rounds display
    const roundsStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 48,
      fontWeight: 'bold',
      fill: 0xFFD700,
      stroke: { color: 0x3E2723, width: 3 }
    });
    
    this.roundsText = new Text({ text: '10', style: roundsStyle });
    this.roundsText.anchor.set(0.5);
    this.roundsText.x = 0;
    this.roundsText.y = -20;
    this.contentContainer.addChild(this.roundsText);
    
    // Decrease button
    const decreaseBtn = new Button('-', 60, 60, {
      backgroundColor: 0x654321,
      hoverColor: 0x8B4513,
      fontSize: 32
    });
    decreaseBtn.x = -100;
    decreaseBtn.y = -20;
    decreaseBtn.setCallback(() => this.decreaseRounds());
    this.contentContainer.addChild(decreaseBtn);
    
    // Increase button
    const increaseBtn = new Button('+', 60, 60, {
      backgroundColor: 0x654321,
      hoverColor: 0x8B4513,
      fontSize: 32
    });
    increaseBtn.x = 100;
    increaseBtn.y = -20;
    increaseBtn.setCallback(() => this.increaseRounds());
    this.contentContainer.addChild(increaseBtn);
    
    // Quick select buttons
    const quickValues = [5, 10, 25, 50, 100];
    const quickStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 14,
      fill: COLORS.TEXT_PRIMARY
    });
    
    const quickLabel = new Text({ text: 'Quick select:', style: quickStyle });
    quickLabel.anchor.set(0.5, 0);
    quickLabel.x = 0;
    quickLabel.y = 50;
    this.contentContainer.addChild(quickLabel);
    
    quickValues.forEach((value, index) => {
      const btn = new Button(String(value), 60, 35, {
        backgroundColor: value === this.rounds ? 0xDAA520 : 0x8B4513,
        hoverColor: 0xDAA520,
        fontSize: 14
      });
      btn.x = -200 + index * 85;
      btn.y = 90;
      btn.setCallback(() => this.setRounds(value));
      this.contentContainer.addChild(btn);
    });
  }
  
  private createButtons(): void {
    // Start button
    const startBtn = new Button('▶ Start', 140, 50, {
      backgroundColor: 0x2E7D32,
      hoverColor: 0x4CAF50,
      fontSize: 18
    });
    startBtn.x = -80;
    startBtn.y = 150;
    startBtn.setCallback(() => this.confirm());
    this.contentContainer.addChild(startBtn);
    
    // Cancel button
    const cancelBtn = new Button('✕ Cancel', 140, 50, {
      backgroundColor: 0xC62828,
      hoverColor: 0xE53935,
      fontSize: 18
    });
    cancelBtn.x = 80;
    cancelBtn.y = 150;
    cancelBtn.setCallback(() => this.close());
    this.contentContainer.addChild(cancelBtn);
  }
  
  private createCloseButton(): void {
    this.closeButton = new Button('✕', 40, 40, {
      backgroundColor: 0xC62828,
      hoverColor: 0xE53935,
      fontSize: 24
    });
    this.closeButton.x = 220;
    this.closeButton.y = -160;
    this.closeButton.setCallback(() => this.close());
    this.contentContainer.addChild(this.closeButton);
  }
  
  private increaseRounds(): void {
    if (this.rounds < 1000) {
      this.rounds += 5;
      this.updateRoundsDisplay();
    }
  }
  
  private decreaseRounds(): void {
    if (this.rounds > 5) {
      this.rounds -= 5;
      this.updateRoundsDisplay();
    }
  }
  
  private setRounds(value: number): void {
    this.rounds = value;
    this.updateRoundsDisplay();
  }
  
  private updateRoundsDisplay(): void {
    this.roundsText.text = String(this.rounds);
  }
  
  private confirm(): void {
    if (this.onConfirmCallback) {
      this.onConfirmCallback(this.rounds);
    }
    this.close();
  }
  
  public onConfirm(callback: (rounds: number) => void): void {
    this.onConfirmCallback = callback;
  }
  
  public open(): void {
    this.rounds = 10;
    this.updateRoundsDisplay();
    this.visible = true;
  }
  
  public close(): void {
    this.visible = false;
  }
  
  public toggle(): void {
    if (this.visible) {
      this.close();
    } else {
      this.open();
    }
  }
}